import {
  useGetABOProfileQuery,
  useGetAboLineageQuery,
  useGetProfileNotesQuery,
} from "../../../../services/AboProfile/AboProfileAPI";
import BasicInfo from "../BasicInfo/BasicInfo";
import ContactActions from "../ContactActions/ContactActions";
import "./ProfileBanner.scss";
import { ErrorTile } from "../../../../common/components/ErrorTile/ErrorTile";
import { useAppSelector } from "../../../../store";
import DownlineProfileIcon, {
  isRootLineage,
} from "../../../../common/components/DownlineProfileLOS/DownlineProfileIcon";
import { KPI_ID } from "../../../../services/AboEnrichment/AboEnrichmentAPI.types";

const PROFILE_BANNER_LINEAGE_KPIS = [KPI_ID.ABO_NAME, KPI_ID.PERSONAL_PV, KPI_ID.BONUS_PERCENT, KPI_ID.PHONE_NUMBER];

interface ProfileBannerProps {
  pushToHistory?: (affAbo: string) => void;
}

const ProfileBanner = ({ pushToHistory }: ProfileBannerProps) => {
  const selectedAffAbo = useAppSelector((s) => s.downlineProfile.affAbo);
  const [aff = "", abo = ""] = selectedAffAbo.split("-");
  const isFromEdLos = useAppSelector((s) => s.profile.isFromEdLos);
  const edlosAboMeta = useAppSelector((s) => s.profile.edlosAboMeta);
  const edlosRootAffAbo = useAppSelector((s) => s.profile.edlosRootAffAbo);
  const edlosRootDepth = useAppSelector((s) => s.profile.edlosRootDepth);

  const { data, isFetching, isError, refetch } = useGetABOProfileQuery(
    { aff, abo, ...(isFromEdLos ? { includePlatinumAndAbove: true } : {}) },
    { skip: !aff || !abo },
  );

  const { data: notesData } = useGetProfileNotesQuery({ affabo: `${aff}-${abo}` }, { skip: !aff || !abo });
  const hasNotes = (notesData?.notes?.length ?? 0) > 0;

  const {
    aboInfo,
    partyInfo,
    additionalRegInfo,
    isPlatinumOrAbove = false,
    isEmeraldAndAbove = false,
    isDiamondAndAbove = false,
    pinAwardSummary,
  } = data ?? {};
  const showAutoRenewal = useAppSelector(
    (state) => state.boot.configuration.profile?.additionalRegInfo?.showAutoRenewal ?? true,
  );

  const { data: lineageData } = useGetAboLineageQuery(
    {
      aff,
      abo,
      IsEdlos: isFromEdLos,
      ...(isFromEdLos && edlosRootAffAbo ? { EdlosRootAffAbo: edlosRootAffAbo } : {}),
      ...(isFromEdLos && edlosRootDepth !== null ? { EdlosRootDepth: edlosRootDepth } : {}),
    },
    { skip: !aff || !abo },
  );

  if (isError)
    return (
      <div className="profileBanner">
        <ErrorTile
          errorStatus={isError}
          isLoading={isFetching}
          clickFunction={() => refetch()}
          tealiumTrackId="downline-profile-banner"
        />
      </div>
    );

  return (
    <>
      <div className="profileBanner">
        <DownlineProfileIcon
          aff={aff}
          abo={abo}
          pushToHistory={pushToHistory}
          kpis={PROFILE_BANNER_LINEAGE_KPIS}
          imageRefUrl={aboInfo?.imageRefUrl}
        />
        <BasicInfo
          aboInfo={aboInfo}
          tags={{
            isOnAutoRenewal: showAutoRenewal && additionalRegInfo?.autoRenewal,
            isOnSop: false,
            hasNotes: hasNotes,
          }}
          award={{
            highestAward: pinAwardSummary?.previousPYPin?.code,
            currentAward: pinAwardSummary?.currentPin?.code,
            currentAwardDate: pinAwardSummary?.currentPin?.qualPeriod,
            isPlatinumOrAbove,
            isEmeraldAndAbove,
            isDiamondAndAbove,
          }}
          isLoading={isFetching}
          isConfidential={aboInfo?.privacyFlag}
          isRootAbo={isRootLineage(lineageData?.lineage)}
          isFromEdLos={isFromEdLos}
          edlosAboMeta={edlosAboMeta}
        />
        <div className="profileBanner__spacer" aria-hidden="true"></div>
        <ContactActions
          partyInfo={partyInfo}
          isLoading={isFetching}
          isConfidential={aboInfo?.privacyFlag}
          isKoreaMarket={aboInfo?.countryCode?.toUpperCase() === "KR"}
        />
      </div>
    </>
  );
};

export default ProfileBanner;
